/**
 * 공용 서버 상태 훅
 * ---------------------------------------------------------------------------
 * 여러 화면이 함께 쓰는 조회를 한곳에 모은다.
 * 쿼리 키를 여기서 관리해야 변경 후 무효화 대상을 빠뜨리지 않는다.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, type NormalizedError } from './api-client';

/* -------------------------------------------------------------------------
 * 쿼리 키
 * ---------------------------------------------------------------------- */

export const qk = {
  users: (params: unknown) => ['users', params] as const,
  departments: ['departments'] as const,
  roles: ['roles'] as const,
  codes: (groups: string[]) => ['codes', groups.join(',')] as const,
  tenant: ['tenant'] as const,
  tenantPolicy: ['tenant', 'policy'] as const,
  supportAccess: ['tenant', 'support-access'] as const,
  me: ['me'] as const,
  sessions: ['sessions'] as const,
};

/* -------------------------------------------------------------------------
 * 공통코드
 * ---------------------------------------------------------------------- */

export interface CodeItem {
  itemCode: string;
  itemName: string;
  itemValue: string | null;
  sortOrder: number;
}

/** 여러 코드 그룹을 한 번에 가져온다. 화면 진입 시 왕복을 줄인다. */
export function useCodes(groups: string[]) {
  return useQuery<Record<string, CodeItem[]>, NormalizedError>({
    queryKey: qk.codes(groups),
    queryFn: async () => {
      const { data } = await apiClient.get<Record<string, CodeItem[]>>('/config/codes', {
        params: { groups: groups.join(',') },
      });
      return data;
    },
    // 공통코드는 거의 바뀌지 않는다. 캐시를 길게 잡는다.
    staleTime: 10 * 60 * 1000,
  });
}

/* -------------------------------------------------------------------------
 * 조직(부서)
 * ---------------------------------------------------------------------- */

export interface DepartmentNode {
  departmentId: string;
  parentId: string | null;
  deptCode: string;
  deptName: string;
  deptPath: string;
  depth: number;
  sortOrder: number;
  memberCount: number;
  isActive: boolean;
  children: DepartmentNode[];
}

export function useDepartments(includeInactive = false) {
  return useQuery<DepartmentNode[], NormalizedError>({
    queryKey: [...qk.departments, includeInactive],
    queryFn: async () => {
      const { data } = await apiClient.get<DepartmentNode[]>('/tenant/departments', {
        params: { includeInactive },
      });
      return data;
    },
  });
}

/** 트리를 Select 용 평면 목록으로 펼친다. 들여쓰기로 계층을 표현한다. */
export function flattenDepartments(
  nodes: DepartmentNode[],
  depth = 0,
): Array<{ value: string; label: string; depth: number }> {
  return nodes.flatMap((node) => [
    { value: node.departmentId, label: `${'　'.repeat(depth)}${node.deptName}`, depth },
    ...flattenDepartments(node.children, depth + 1),
  ]);
}

/* -------------------------------------------------------------------------
 * 역할
 * ---------------------------------------------------------------------- */

export interface RoleSummary {
  roleId: string;
  roleCode: string;
  roleName: string;
  description: string | null;
  isSystem: boolean;
  isDefault: boolean;
  userCount: number;
  permissions: string[];
}

export function useRoles() {
  return useQuery<RoleSummary[], NormalizedError>({
    queryKey: qk.roles,
    queryFn: async () => {
      const { data } = await apiClient.get<RoleSummary[]>('/roles');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/* -------------------------------------------------------------------------
 * 변경 후 무효화
 * ---------------------------------------------------------------------- */

/**
 * 구성원 변경 뮤테이션.
 *
 * 이름이 바뀌면 서버의 가나다 정렬 캐시 버전이 올라가므로,
 * 목록 쿼리를 반드시 무효화해야 낡은 순서가 남지 않는다.
 */
export function useInvalidateUsers() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['users'] });
    void client.invalidateQueries({ queryKey: qk.departments });
  };
}

export function useApiMutation<TInput, TOutput = void>(
  fn: (input: TInput) => Promise<TOutput>,
  options: { onSuccess?: (data: TOutput) => void } = {},
) {
  return useMutation<TOutput, NormalizedError, TInput>({
    mutationFn: fn,
    onSuccess: options.onSuccess,
  });
}

/** 서버가 필드별 오류를 주면 사람이 읽을 한 줄로 합친다. */
export function errorText(err: NormalizedError | null | undefined): string {
  if (!err) return '';
  if (err.details) {
    const parts = Object.values(err.details).flat();
    if (parts.length > 0) return parts.join(' ');
  }
  return err.message;
}
